import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DollarSign, 
  Car, 
  Clock, 
  Users,
  TrendingUp,
  Settings,
  Calculator,
  MapPin,
  Activity,
  Calendar,
  Loader2,
  Save,
  User,
  Filter,
  Package,
  Pill,
  Box,
  ChevronDown,
  ChevronUp,
  FileDown,
  Briefcase,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  HeartPulse,
  CircleDollarSign,
  Wallet,
  BadgeCheck,
  BadgeAlert,
  PiggyBank,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const Custos = () => {
  const { user, api, canViewServiceRevenue, canViewServicePayout, canViewStockCosts, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pacienteStats, setPacienteStats] = useState([]);
  const [consumoPaciente, setConsumoPaciente] = useState([]);
  const [servicosAnalytics, setServicosAnalytics] = useState(null);
  const [saudeFinanceira, setSaudeFinanceira] = useState(null);
  const [configModal, setConfigModal] = useState(false);
  const [custoConfig, setCustoConfig] = useState({
    custo_por_km: 1.50,
    tempo_por_visita_min: 40
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [viewMode, setViewMode] = useState('resumo'); // 'resumo', 'por_paciente'
  const [showEstoqueDetails, setShowEstoqueDetails] = useState(false);
  const [showServicosDetails, setShowServicosDetails] = useState(false);
  const [showSaudeDetails, setShowSaudeDetails] = useState(false);
  const [showAllPacientes, setShowAllPacientes] = useState(false);
  const [allPacientes, setAllPacientes] = useState([]);
  
  // Period filters
  const [periodo, setPeriodo] = useState('sempre');
  const [appliedPeriodo, setAppliedPeriodo] = useState('sempre');

  // Merge all patients from top_lucrativos and top_deficitarios
  useEffect(() => {
    if (saudeFinanceira) {
      const lucrativos = saudeFinanceira.top_lucrativos || [];
      const deficitarios = saudeFinanceira.top_deficitarios || [];
      const all = [...lucrativos, ...deficitarios];
      // Sort by lucro descending
      all.sort((a, b) => (b.lucro_liquido || 0) - (a.lucro_liquido || 0));
      setAllPacientes(all);
    }
  }, [saudeFinanceira]);

  useEffect(() => {
    fetchData();
    fetchConfig();
  }, []);

  const fetchData = async (periodoParam = appliedPeriodo) => {
    try {
      setLoading(true);
      const [statsRes, pacientesRes, consumoRes, servicosRes, saudeRes] = await Promise.all([
        api.get(`/custos/resumo?periodo=${periodoParam}`),
        api.get(`/custos/por-paciente?periodo=${periodoParam}`),
        api.get(`/estoque/consumo-por-paciente?periodo=${periodoParam}`).catch(() => ({ data: [] })),
        api.get(`/custos/servicos?periodo=${periodoParam}`).catch(() => ({ data: null })),
        api.get(`/financeiro/saude?periodo=${periodoParam}`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPacienteStats(pacientesRes.data);
      setConsumoPaciente(consumoRes.data || []);
      setServicosAnalytics(servicosRes.data || null);
      setSaudeFinanceira(saudeRes.data || null);
    } catch (error) {
      console.error('Error fetching cost data:', error);
      toast.error('Erro ao carregar dados de custos');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setAppliedPeriodo(periodo);
    fetchData(periodo);
    toast.success('Filtros aplicados!');
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/custos/config');
      if (response.data) {
        setCustoConfig(response.data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/custos/config', custoConfig);
      toast.success('Configurações salvas!');
      setConfigModal(false);
      fetchData(appliedPeriodo); // Refresh stats with new config
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const getPeriodoLabel = (p) => {
    switch(p) {
      case '7d': return 'Últimos 7 dias';
      case 'mes': return 'Mês atual';
      case 'sempre': return 'Sempre';
      default: return 'Sempre';
    }
  };

  // Check if user can view financial values
  const canViewFinancials = isAdmin || canViewServiceRevenue || canViewServicePayout || canViewStockCosts;

  const formatCurrency = (value) => {
    // Hide monetary values from non-admins
    if (!canViewFinancials) {
      return '---';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0h 0min';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins}min`;
    return `${hours}h ${mins}min`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope']">Custos e Estatísticas</h1>
            <p className="text-muted-foreground">
              Acompanhe os custos operacionais e tempo de atendimento
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => setConfigModal(true)} className="gap-2">
              <Settings className="h-4 w-4" />
              Configurar Valores
            </Button>
          )}
        </div>

        {/* Warning for non-admins */}
        {!canViewFinancials && (
          <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20 p-4">
            <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="h-5 w-5" />
              <span>Valores financeiros estão ocultos. Entre em contato com um administrador se precisar de acesso.</span>
            </div>
          </Card>
        )}

        {/* Period Filter */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 max-w-xs">
              <Label className="mb-2 block">Período</Label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Últimos 7 dias</SelectItem>
                  <SelectItem value="mes">Mês atual</SelectItem>
                  <SelectItem value="sempre">Sempre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyFilters} className="gap-2">
              <Filter className="h-4 w-4" />
              APLICAR FILTROS
            </Button>
            {appliedPeriodo !== 'sempre' && (
              <Badge variant="secondary" className="h-9 px-3 flex items-center">
                Exibindo: {getPeriodoLabel(appliedPeriodo)}
              </Badge>
            )}
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Car className="h-4 w-4" />
                KM Percorridos (Total)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(stats?.total_km || 0).toFixed(1)} km</div>
              <p className="text-xs text-muted-foreground">
                Média: {(stats?.media_km_por_dia || 0).toFixed(1)} km/dia
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Total de Visitas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_visitas || 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.pacientes_ativos || 0} pacientes ativos
              </p>
            </CardContent>
          </Card>

          <Card className="border-primary/50 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Total de Diárias
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>1 diária = 1 dia de cuidado prestado</p>
                      <p className="text-xs">Primeiro dia de internação conta como D+1</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{stats?.total_diarias || 0}</div>
              <p className="text-xs text-muted-foreground">
                diárias prestadas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tempo em Trânsito
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatTime(stats?.tempo_transito_total_min)}</div>
              <p className="text-xs text-muted-foreground">
                Média: {formatTime(stats?.tempo_transito_medio_dia_min)}/dia
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Custo Total Estimado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(stats?.custo_total)}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(custoConfig.custo_por_km)}/km
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Inventory Consumption Summary */}
        {consumoPaciente.length > 0 && (
          <Card className="border-orange-200 dark:border-orange-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-orange-600" />
                Consumo de Materiais e Medicamentos
              </CardTitle>
              <CardDescription>
                Custos de estoque por paciente no período selecionado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total de consumo (estoque)</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {formatCurrency(consumoPaciente.reduce((acc, p) => acc + (p.custo_total || 0), 0))}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowEstoqueDetails(!showEstoqueDetails)}
                  className="gap-2"
                >
                  {showEstoqueDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showEstoqueDetails ? 'Ocultar' : 'Ver detalhes'}
                </Button>
              </div>
              
              {showEstoqueDetails && (
                <div className="space-y-3">
                  {consumoPaciente.map((pac) => (
                    <div key={pac.internacao_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{pac.paciente_nome}</span>
                        </div>
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          {formatCurrency(pac.custo_total)}
                        </Badge>
                      </div>
                      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                        {pac.itens?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              {item.nome?.toLowerCase().includes('medicamento') || item.nome?.toLowerCase().includes('mg') ? (
                                <Pill className="h-3 w-3 text-purple-500" />
                              ) : (
                                <Box className="h-3 w-3 text-blue-500" />
                              )}
                              <span className="truncate max-w-[150px]">{item.nome}</span>
                            </div>
                            <span className="text-muted-foreground">{formatCurrency(item.custo)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Services Revenue & Cost Summary */}
        {servicosAnalytics && (
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Serviços Profissionais
              </CardTitle>
              <CardDescription>
                Receitas e custos de serviços contratados (fisioterapia, enfermagem, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-muted-foreground">Serviços Ativos</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {servicosAnalytics.resumo?.total_servicos_ativos || 0}
                  </p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <ArrowDownRight className="h-3 w-3" /> Receita Total
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(servicosAnalytics.resumo?.total_receita || 0)}
                  </p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <ArrowUpRight className="h-3 w-3" /> Custo Total
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(servicosAnalytics.resumo?.total_custo || 0)}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm text-muted-foreground">Margem</p>
                  <p className={`text-2xl font-bold ${(servicosAnalytics.resumo?.margem || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(servicosAnalytics.resumo?.margem || 0)}
                    <span className="text-sm font-normal ml-1">
                      ({servicosAnalytics.resumo?.margem_percentual || 0}%)
                    </span>
                  </p>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowServicosDetails(!showServicosDetails)}
                className="gap-2 mb-4"
              >
                {showServicosDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showServicosDetails ? 'Ocultar detalhes' : 'Ver por tipo de serviço'}
              </Button>
              
              {showServicosDetails && servicosAnalytics.por_tipo?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Por Tipo de Serviço</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Serviço</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Receita</TableHead>
                          <TableHead className="text-right">Custo</TableHead>
                          <TableHead className="text-right">Margem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {servicosAnalytics.por_tipo.map((servico, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{servico.nome}</TableCell>
                            <TableCell className="text-center">{servico.quantidade}</TableCell>
                            <TableCell className="text-right text-green-600">{formatCurrency(servico.receita)}</TableCell>
                            <TableCell className="text-right text-red-600">{formatCurrency(servico.custo)}</TableCell>
                            <TableCell className={`text-right font-medium ${(servico.receita - servico.custo) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(servico.receita - servico.custo)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Financial Health Dashboard */}
        {saudeFinanceira && (
          <Card className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50/50 to-transparent dark:from-emerald-900/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HeartPulse className="h-5 w-5 text-emerald-600" />
                Saúde Financeira
              </CardTitle>
              <CardDescription>
                Resultado consolidado: Receitas - Custos de Serviços - Custos de Materiais = Lucro
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Main KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CircleDollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(saudeFinanceira.resumo?.total_receita || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Receita Total</p>
                </div>
                
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Briefcase className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-xl font-bold text-blue-600">
                    {formatCurrency(saudeFinanceira.resumo?.total_custo_servicos || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Custo Serviços</p>
                </div>
                
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Package className="h-4 w-4 text-orange-600" />
                  </div>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(saudeFinanceira.resumo?.total_custo_materiais || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Custo Materiais</p>
                </div>
                
                <div className={`p-4 rounded-lg text-center ${
                  (saudeFinanceira.resumo?.lucro_liquido || 0) >= 0 
                    ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <PiggyBank className={`h-4 w-4 ${
                      (saudeFinanceira.resumo?.lucro_liquido || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`} />
                  </div>
                  <p className={`text-xl font-bold ${
                    (saudeFinanceira.resumo?.lucro_liquido || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(saudeFinanceira.resumo?.lucro_liquido || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Lucro Líquido</p>
                </div>
                
                <div className={`p-4 rounded-lg text-center ${
                  (saudeFinanceira.resumo?.margem_geral || 0) >= 0 
                    ? 'bg-purple-50 dark:bg-purple-900/20' 
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <p className={`text-xl font-bold ${
                    (saudeFinanceira.resumo?.margem_geral || 0) >= 0 ? 'text-purple-600' : 'text-red-600'
                  }`}>
                    {saudeFinanceira.resumo?.margem_geral || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Margem Geral</p>
                </div>
              </div>
              
              {/* Patient Distribution */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-bold">{saudeFinanceira.resumo?.total_pacientes || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Pacientes</p>
                  </div>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-3">
                  <BadgeCheck className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-bold text-green-600">{saudeFinanceira.resumo?.pacientes_lucrativos || 0}</p>
                    <p className="text-xs text-muted-foreground">Lucrativos</p>
                  </div>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-3">
                  <BadgeAlert className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-bold text-red-600">{saudeFinanceira.resumo?.pacientes_deficitarios || 0}</p>
                    <p className="text-xs text-muted-foreground">Deficitários</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 mb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowSaudeDetails(!showSaudeDetails)}
                  className="gap-2"
                >
                  {showSaudeDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showSaudeDetails ? 'Ocultar detalhes' : 'Ver por paciente'}
                </Button>
                <Button 
                  variant={showAllPacientes ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowAllPacientes(!showAllPacientes)}
                  className="gap-2"
                  data-testid="ver-todos-pacientes-btn"
                >
                  <Users className="h-4 w-4" />
                  {showAllPacientes ? 'Ocultar lista completa' : 'Ver todos os pacientes'}
                </Button>
              </div>

              {/* Complete patient list */}
              {showAllPacientes && allPacientes.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4" />
                    Resumo Financeiro por Paciente ({allPacientes.length} pacientes)
                  </h4>
                  <div className="border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="w-[200px]">Paciente</TableHead>
                          <TableHead className="text-right">Receita Serviços</TableHead>
                          <TableHead className="text-right">Custo Serviços</TableHead>
                          <TableHead className="text-right">Custo Materiais</TableHead>
                          <TableHead className="text-right">Total Custos</TableHead>
                          <TableHead className="text-right">Lucro/Prejuízo</TableHead>
                          <TableHead className="text-right">Margem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allPacientes.map((pac, idx) => {
                          const totalCustos = (pac.custo_servicos || 0) + (pac.custo_materiais || 0);
                          const isPositive = (pac.lucro_liquido || 0) >= 0;
                          return (
                            <TableRow key={idx} className={isPositive ? '' : 'bg-red-50/30 dark:bg-red-900/10'}>
                              <TableCell className="font-medium">{pac.paciente_nome}</TableCell>
                              <TableCell className="text-right text-green-600">
                                {formatCurrency(pac.receita_servicos || 0)}
                              </TableCell>
                              <TableCell className="text-right text-blue-600">
                                {formatCurrency(pac.custo_servicos || 0)}
                              </TableCell>
                              <TableCell className="text-right text-orange-600">
                                {formatCurrency(pac.custo_materiais || 0)}
                              </TableCell>
                              <TableCell className="text-right text-red-600 font-medium">
                                {formatCurrency(totalCustos)}
                              </TableCell>
                              <TableCell className={`text-right font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(pac.lucro_liquido || 0)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge 
                                  variant="outline" 
                                  className={isPositive 
                                    ? 'bg-green-50 text-green-700 border-green-300' 
                                    : 'bg-red-50 text-red-700 border-red-300'
                                  }
                                >
                                  {pac.margem_percentual || 0}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Total: {formatCurrency(allPacientes.reduce((sum, p) => sum + (p.receita_servicos || 0), 0))} em receitas, {' '}
                    {formatCurrency(allPacientes.reduce((sum, p) => sum + (p.custo_servicos || 0) + (p.custo_materiais || 0), 0))} em custos
                  </p>
                </div>
              )}
              
              {showSaudeDetails && (
                <div className="space-y-4">
                  {/* Top Profitable */}
                  {saudeFinanceira.top_lucrativos?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm text-green-700 flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4" />
                        Top Pacientes Lucrativos
                      </h4>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Paciente</TableHead>
                              <TableHead className="text-right">Receita</TableHead>
                              <TableHead className="text-right">Custos</TableHead>
                              <TableHead className="text-right">Lucro</TableHead>
                              <TableHead className="text-right">Margem</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {saudeFinanceira.top_lucrativos.slice(0, 5).map((pac, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{pac.paciente_nome}</TableCell>
                                <TableCell className="text-right text-green-600">{formatCurrency(pac.receita_servicos)}</TableCell>
                                <TableCell className="text-right text-red-600">
                                  {formatCurrency(pac.custo_servicos + pac.custo_materiais)}
                                </TableCell>
                                <TableCell className="text-right font-bold text-emerald-600">
                                  {formatCurrency(pac.lucro_liquido)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                    {pac.margem_percentual}%
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  {/* Top Deficitarios */}
                  {saudeFinanceira.top_deficitarios?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm text-red-700 flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4" />
                        Pacientes com Prejuízo (Atenção!)
                      </h4>
                      <div className="border border-red-200 rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-red-50 dark:bg-red-900/20">
                              <TableHead>Paciente</TableHead>
                              <TableHead className="text-right">Receita</TableHead>
                              <TableHead className="text-right">Custos</TableHead>
                              <TableHead className="text-right">Prejuízo</TableHead>
                              <TableHead className="text-right">Margem</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {saudeFinanceira.top_deficitarios.slice(0, 5).map((pac, idx) => (
                              <TableRow key={idx} className="bg-red-50/30 dark:bg-red-900/10">
                                <TableCell className="font-medium">{pac.paciente_nome}</TableCell>
                                <TableCell className="text-right text-green-600">{formatCurrency(pac.receita_servicos)}</TableCell>
                                <TableCell className="text-right text-red-600">
                                  {formatCurrency(pac.custo_servicos + pac.custo_materiais)}
                                </TableCell>
                                <TableCell className="text-right font-bold text-red-600">
                                  {formatCurrency(pac.lucro_liquido)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                                    {pac.margem_percentual}%
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* View Mode Selector */}
        <div className="flex justify-end">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Visualizar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resumo">Resumo geral</SelectItem>
              <SelectItem value="por_paciente">Por paciente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Time Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Distribuição de Tempo
              </CardTitle>
              <CardDescription>
                Tempo total gasto em transporte e visitas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Car className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">Tempo em Trânsito</div>
                      <div className="text-sm text-muted-foreground">Deslocamento entre visitas</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatTime(stats?.tempo_transito_total_min)}</div>
                    <div className="text-xs text-muted-foreground">{((stats?.tempo_transito_total_min || 0) / ((stats?.tempo_transito_total_min || 0) + (stats?.tempo_visita_total_min || 1)) * 100).toFixed(0)}%</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium">Tempo em Visitas</div>
                      <div className="text-sm text-muted-foreground">{custoConfig.tempo_por_visita_min} min por visita</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatTime(stats?.tempo_visita_total_min)}</div>
                    <div className="text-xs text-muted-foreground">{((stats?.tempo_visita_total_min || 0) / ((stats?.tempo_transito_total_min || 0) + (stats?.tempo_visita_total_min || 1)) * 100).toFixed(0)}%</div>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Tempo Total Operacional</span>
                    <span className="font-bold text-lg">{formatTime((stats?.tempo_transito_total_min || 0) + (stats?.tempo_visita_total_min || 0))}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Detalhamento de Custos
              </CardTitle>
              <CardDescription>
                Custos baseados na distância percorrida
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Car className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="font-medium">Custo de Combustível</div>
                      <div className="text-sm text-muted-foreground">{(stats?.total_km || 0).toFixed(1)} km × {formatCurrency(custoConfig.custo_por_km)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(stats?.custo_combustivel)}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Custo Médio por Paciente</div>
                      <div className="text-sm text-muted-foreground">{stats?.pacientes_ativos || 0} pacientes</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(stats?.custo_medio_por_paciente)}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <Calendar className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <div className="font-medium">Custo Médio Diário</div>
                      <div className="text-sm text-muted-foreground">Por dia de operação</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(stats?.custo_medio_diario)}</div>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Custo Total Acumulado</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(stats?.custo_total)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Per-Patient Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Custos por Paciente
            </CardTitle>
            <CardDescription>
              Detalhamento de KM e tempo gasto por cada paciente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paciente</TableHead>
                    <TableHead className="text-center">Dias Int.</TableHead>
                    <TableHead className="text-center">Visitas</TableHead>
                    <TableHead className="text-right">KM Total</TableHead>
                    <TableHead className="text-right">Tempo Trânsito</TableHead>
                    <TableHead className="text-right">Tempo Visita</TableHead>
                    <TableHead className="text-right">Custo Est.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pacienteStats.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        Nenhum dado de custo disponível
                      </TableCell>
                    </TableRow>
                  ) : (
                    pacienteStats.map((paciente) => (
                      <TableRow key={paciente.internacao_id}>
                        <TableCell>
                          <div className="font-medium">{paciente.paciente_nome}</div>
                          <div className="text-xs text-muted-foreground">{paciente.equipe_nome || 'Sem equipe'}</div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">D+{paciente.dias_internacao}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{paciente.total_visitas}</TableCell>
                        <TableCell className="text-right font-medium">{(paciente.km_total || 0).toFixed(1)} km</TableCell>
                        <TableCell className="text-right">{formatTime(paciente.tempo_transito_min)}</TableCell>
                        <TableCell className="text-right">{formatTime(paciente.tempo_visita_min)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCurrency(paciente.custo_estimado)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Config Modal */}
        <Dialog open={configModal} onOpenChange={setConfigModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configurar Valores de Custo
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Custo por KM (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={custoConfig.custo_por_km}
                  onChange={(e) => setCustoConfig({...custoConfig, custo_por_km: parseFloat(e.target.value) || 0})}
                  placeholder="1.50"
                />
                <p className="text-xs text-muted-foreground">
                  Valor por quilômetro para cálculo de custo de combustível
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tempo por Visita (minutos)</Label>
                <Input
                  type="number"
                  min="1"
                  value={custoConfig.tempo_por_visita_min}
                  onChange={(e) => setCustoConfig({...custoConfig, tempo_por_visita_min: parseInt(e.target.value) || 40})}
                  placeholder="40"
                />
                <p className="text-xs text-muted-foreground">
                  Tempo médio estimado para cada visita de enfermagem
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfigModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveConfig} disabled={savingConfig} className="gap-2">
                {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Custos;
